import classNames from "classnames";
import { Immutable } from "immer";
import React, { useEffect, useRef, useState } from "react";
import { t } from "ttag";

import { getProductURL } from "../../../api/products";
import { ErrorModal } from "../../../components/ErrorModal";
import { AddToCartType, ConfiguratorTypes } from "../../../constants";
import {
    IAddon,
    IBasket,
    IBasketLine,
    IConcreteBundle,
    IProduct,
} from "../../../models/catalogue";
import { WebPageURL } from "../../../models/nominals";
import { reloadBasket } from "../../../store/basket/thunks";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { prefetchCSRFToken } from "../../../utils/ajax";
import { trackAddToBasketEvent } from "../../../utils/analytics";
import { addProductToBasket } from "../../../utils/basket";
import { getHighestDiscountName } from "../../../utils/money";
import { usePageSetting } from "../../../utils/settings";
import { navigateToURL, usePageURL } from "../../../utils/urls";
import { defaults } from "../defaults";
import { loadUserConfigurableBundles } from "../loaders";
import { IUpsellModalComponentFn, IUpsellModalComponentRef } from "../models";
import {
    setAddToBasketBtnState,
    setAddToBasketErrorModalState,
} from "../reducers";
import {
    addonVariantsSelector,
    rootProductSelector,
    upgradedVariantSelector,
} from "../selectors";
import { isPreorderProduct } from "../utils";

const onCloseErrorModal = (dispatch: ReturnType<typeof useAppDispatch>) => {
    dispatch(setAddToBasketErrorModalState({ isOpen: false, reason: "" }));
};

const onBeforeAddToBasket = (dispatch: ReturnType<typeof useAppDispatch>) => {
    // Disable buttons
    dispatch(setAddToBasketBtnState({ cooldownActive: true, text: t`Adding` }));
};

const onAfterAddToBasket = (
    dispatch: ReturnType<typeof useAppDispatch>,
    basket: IBasket | null,
    upgradedVariant: IProduct | null,
    quantity: number,
    selectedAddonVariants: IAddon[],
    concreteBundles: readonly IConcreteBundle[],
    showUpsellModal: ((product: IProduct | null) => boolean) | undefined,
    upsellModalRef: React.RefObject<IUpsellModalComponentRef | null>,
    basketLink: WebPageURL,
    configureGiftsLink: WebPageURL,
) => {
    // Re-enable buttons
    dispatch(setAddToBasketBtnState({ cooldownActive: false, text: t`Added` }));

    // Refresh the shared basket state so the header cart badge / mini-cart
    // reflect the add when no navigation follows (e.g. the upsell modal path).
    void dispatch(reloadBasket());

    // Send an `add_to_cart` event to the data layer
    const matchingLine = basket?.lines.find(
        (line: IBasketLine) => line.product.id === upgradedVariant?.id,
    );
    trackAddToBasketEvent(
        upgradedVariant,
        quantity,
        matchingLine ? AddToCartType.INCREASE_QUANTITY : AddToCartType.ADDED,
        matchingLine ? getHighestDiscountName(matchingLine.discounts) : null,
    );

    // Show the upsell modal?
    if (
        selectedAddonVariants.length === 0 &&
        upsellModalRef.current &&
        concreteBundles.length > 0 &&
        showUpsellModal &&
        showUpsellModal(upgradedVariant)
    ) {
        upsellModalRef.current.openModal();
        return;
    }

    // Redirect customer to the basket page
    redirectToNextPage(basketLink, configureGiftsLink);
};

const onAddToBasketError = (
    dispatch: ReturnType<typeof useAppDispatch>,
    upgradedVariant: IProduct | null,
    reason: string,
) => {
    // Re-enable buttons
    const isPreorder = upgradedVariant && isPreorderProduct(upgradedVariant);
    const addToBasketButtonText = isPreorder ? t`Pre-Order` : t`Add to Cart`;
    dispatch(
        setAddToBasketBtnState({
            cooldownActive: false,
            text: addToBasketButtonText,
        }),
    );
    dispatch(setAddToBasketErrorModalState({ isOpen: true, reason }));
};

const onAddToBasket = async (
    dispatch: ReturnType<typeof useAppDispatch>,
    basket: IBasket | null,
    upgradedVariant: IProduct | null,
    quantity: number,
    selectedAddonVariants: IAddon[],
    concreteBundles: readonly IConcreteBundle[],
    showUpsellModal: ((product: IProduct | null) => boolean) | undefined,
    upsellModalRef: React.RefObject<IUpsellModalComponentRef | null>,
    basketLink: WebPageURL,
    configureGiftsLink: WebPageURL,
) => {
    onBeforeAddToBasket(dispatch);
    try {
        if (upgradedVariant) {
            await addProductToBasket(upgradedVariant.url, quantity);
        }
        for (const addon of selectedAddonVariants) {
            await addProductToBasket(
                getProductURL(addon.productID),
                addon.quantity,
            );
        }
        onAfterAddToBasket(
            dispatch,
            basket,
            upgradedVariant,
            quantity,
            selectedAddonVariants,
            concreteBundles,
            showUpsellModal,
            upsellModalRef,
            basketLink,
            configureGiftsLink,
        );
    } catch (e) {
        // Display the error to the user
        let reason = t`An unexpected error occurred. Please try again.`;
        try {
            // TODO(i18n): Server error message (e.response.body.reason) may not be translated. This overwrites the translated fallback message with untranslated server response. Server should return translated errors or error codes.
            reason = e.response.body.reason;
        } catch (e2) {
            console.error(e2);
        }
        onAddToBasketError(dispatch, upgradedVariant, reason);
    }
};

const onUpsellProceed = (
    basketLink: WebPageURL,
    configureGiftsLink: WebPageURL,
) => {
    redirectToNextPage(basketLink, configureGiftsLink);
};

async function redirectToNextPage(
    basketLink: WebPageURL,
    configureGiftsLink: WebPageURL,
) {
    // Check if the user has any gifts to configure
    const bundles = await loadUserConfigurableBundles();
    console.log(
        `[Configurator] Found ${bundles.length} user-configurable bundles needing configuration`,
    );

    // Check if there is at least one available gift.
    const isAtLeastOneAvailable = bundles.map((bundle) => {
        const availability = bundle.suggested_range_products.map((p) => {
            if (p.children.length <= 0) {
                return p.availability.is_available_to_buy;
            }
            const variantAvailability = p.children.map((v) => {
                return v.availability.is_available_to_buy;
            });
            return variantAvailability.some((i) => {
                return i;
            });
        });
        return availability.some((i) => {
            return i;
        });
    });

    const nextURL =
        bundles.length <= 0
            ? basketLink
            : isAtLeastOneAvailable
              ? configureGiftsLink
              : basketLink;
    setTimeout(() => {
        navigateToURL(nextURL);
    }, 0);
}

// Deferred until after mount to avoid SSR hydration mismatch —
// UpsellModal queries the DOM via getDocument(), which returns null during SSR.
const buildUpsellModal = (
    hasMounted: boolean,
    getUpsellModalComponentFn:
        | ((product: IProduct) => IUpsellModalComponentFn | null)
        | undefined,
    upgradedVariant: IProduct | null,
    concreteBundles: readonly IConcreteBundle[],
    upsellModalRef: React.RefObject<IUpsellModalComponentRef | null>,
    basketLink: WebPageURL,
    configureGiftsLink: WebPageURL,
) => {
    if (!hasMounted || !getUpsellModalComponentFn || !upgradedVariant) {
        return null;
    }
    const UpsellModal = getUpsellModalComponentFn(upgradedVariant);
    if (!UpsellModal) {
        return null;
    }
    return (
        <UpsellModal
            ref={upsellModalRef}
            bundles={concreteBundles}
            onProceed={() => onUpsellProceed(basketLink, configureGiftsLink)}
        />
    );
};

export const AddToBasketButton = (
    props: Immutable<{
        // Links
        basketLink: WebPageURL;
        configureGiftsLink: WebPageURL;
        // Misc options
        configuratorType: ConfiguratorTypes;
        // is quantitySelector enabled
        enableQuantitySelector?: boolean;
        buttonColor: string;
        // Optional: Up Sell Modal functionality
        showUpsellModal?: (product: IProduct | null) => boolean;
        getUpsellModalComponentFn?: (
            product: IProduct,
        ) => IUpsellModalComponentFn | null;
    }>,
): React.ReactNode => {
    const configuratorState = useAppSelector((rootState) => {
        return rootState.configurator || defaults;
    });
    const basket = useAppSelector(
        (rootState) => rootState.checkout.data.basket,
    );

    const concreteBundles = configuratorState.entities.concreteBundles;
    const rootProduct = rootProductSelector(configuratorState);
    const upgradedVariant = upgradedVariantSelector(configuratorState);
    const quantity = configuratorState.ui.quantity;
    const selectedAddonVariants = addonVariantsSelector(configuratorState);
    const addToBasketCooldownActive =
        configuratorState.ui.addToBasketCooldownActive;
    const addToBasketButtonText = configuratorState.ui.addToBasketButtonText;
    const addToBasketErrorOpen = configuratorState.ui.addToBasketErrorOpen;
    const addToBasketErrorReason = configuratorState.ui.addToBasketErrorReason;
    const priceError = configuratorState.entities.priceError;

    const dispatch = useAppDispatch();

    const findRetailerURL = usePageURL("find_a_retailer");
    const enableEcom = usePageSetting("enable_ecom");

    const upsellModalRef = useRef<IUpsellModalComponentRef>(null);

    // Track mount state for deferring DOM-dependent children (e.g. UpsellModal)
    const [hasMounted, setHasMounted] = useState(false);
    useEffect(() => setHasMounted(true), []);
    useEffect(prefetchCSRFToken, []);

    const isPreorder = upgradedVariant && isPreorderProduct(upgradedVariant);
    const inStoreOnly =
        !enableEcom ||
        (rootProduct &&
            rootProduct.attributes.in_store_only &&
            rootProduct.attributes.in_store_only.value);
    const variantIsAvailableToBuy = !!(
        rootProduct &&
        upgradedVariant &&
        upgradedVariant.availability.is_available_to_buy
    );

    // Build button classnames
    const btnClasses = classNames({
        "configurator__add-button": true,
        [`configurator__add-button--${props.configuratorType}`]: true,
        [`configurator__add-button--${props.configuratorType}--with-qty`]:
            props.enableQuantitySelector,
        "button": true,
        "button--secondary": isPreorder,
        [`button--${props.buttonColor}`]: !isPreorder,
        "al-configurator__add-to-cart": true,
    });
    const btnText = inStoreOnly ? t`Find a Retailer` : addToBasketButtonText;
    const reason = addToBasketErrorReason;

    return (
        <>
            <button
                className={btnClasses}
                disabled={
                    addToBasketCooldownActive ||
                    !variantIsAvailableToBuy ||
                    !!priceError
                }
                onClick={() => {
                    if (inStoreOnly && findRetailerURL) {
                        navigateToURL(findRetailerURL);
                    } else {
                        onAddToBasket(
                            dispatch,
                            basket,
                            upgradedVariant,
                            quantity,
                            selectedAddonVariants,
                            concreteBundles,
                            props.showUpsellModal,
                            upsellModalRef,
                            props.basketLink,
                            props.configureGiftsLink,
                        );
                    }
                }}
            >
                {btnText}
            </button>
            {buildUpsellModal(
                hasMounted,
                props.getUpsellModalComponentFn,
                upgradedVariant,
                concreteBundles,
                upsellModalRef,
                props.basketLink,
                props.configureGiftsLink,
            )}
            <ErrorModal
                name="configurator"
                isOpen={addToBasketErrorOpen}
                onRequestClose={() => onCloseErrorModal(dispatch)}
            >
                <p>{t`Product could not be added to cart (${reason}).`}</p>
                <p>{t`Please adjust your selection and try again.`}</p>
            </ErrorModal>
        </>
    );
};
